import React from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import { 
  UserGroupIcon, 
  BuildingOfficeIcon, 
  CogIcon,
  ChartBarIcon,
  ShieldCheckIcon 
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import LocationManagement from './admin/LocationManagement';
import MasterDataManagement from './admin/MasterDataManagement';
import UserManagement from './admin/UserManagement';
import SystemReports from './admin/SystemReports';
import InventoryPurchases from './admin/InventoryPurchases';
import InventoryBalances from './admin/InventoryBalances';
import GrowthSettings from './admin/GrowthSettings';

// Route guard for admin-only routes
const AdminOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  
  if (user?.role !== 'admin') {
    return (
      <div className="container-mobile py-8">
        <div className="text-center">
          <ShieldCheckIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">This section requires admin privileges.</p>
          <Link 
            to="/admin" 
            className="mt-4 inline-block text-blue-600 hover:text-blue-800 underline"
          >
            Return to Panel
          </Link>
        </div>
      </div>
    );
  }
  
  return <>{children}</>;
};

const SuperAdminOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();

  if (user?.role !== 'admin' || !user?.is_superadmin) {
    return (
      <div className="container-mobile py-8">
        <div className="text-center">
          <ShieldCheckIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Superadmin Access Required</h2>
          <p className="text-gray-600">This report is visible only for superadmin users.</p>
          <Link
            to="/admin"
            className="mt-4 inline-block text-blue-600 hover:text-blue-800 underline"
          >
            Return to Panel
          </Link>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

const AdminMenuItem: React.FC<{
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  color: string;
  to: string;
}> = ({ title, description, icon: Icon, color, to }) => (
  <Link
    to={to}
    className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow duration-200"
  >
    <div className="flex items-center space-x-4">
      <div className={`p-3 rounded-lg ${color}`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
      <div className="flex-1">
        <h3 className="font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600 mt-1">{description}</p>
      </div>
      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </div>
  </Link>
);

const AdminMenu: React.FC = () => {
  const { user } = useAuth();

  // Allow admin and manager roles
  if (user?.role !== 'admin' && user?.role !== 'manager') {
    return (
      <div className="container-mobile py-8">
        <div className="text-center">
          <ShieldCheckIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You need admin or manager privileges to access this section.</p>
        </div>
      </div>
    );
  }

  const isAdmin = user?.role === 'admin';
  const isSuperAdmin = isAdmin && !!user?.is_superadmin;

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="text-center">
        <CogIcon className="h-16 w-16 text-blue-600 mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {isAdmin ? 'Admin Panel' : 'Management Panel'}
        </h1>
        <p className="text-lg text-gray-600">
          {isAdmin 
            ? 'Manage users, locations, and system configuration'
            : 'Access inventory management features from the sidebar'
          }
        </p>
      </div>

      {/* Admin Menu Items */}
      <div className="space-y-4">
        {isAdmin && (
          <>
            <AdminMenuItem
              title="Manage Locations"
              description="Add, edit, and configure farm locations"
              icon={BuildingOfficeIcon}
              color="bg-green-600"
              to="/admin/locations"
            />
            
            <AdminMenuItem
              title="Master Data"
              description="Configure fish species, medicine types, and feed brands"
              icon={CogIcon}
              color="bg-purple-600"
              to="/admin/master-data"
            />
            
            <AdminMenuItem
              title="Manage Users"
              description="Create, edit, and assign user roles and permissions"
              icon={UserGroupIcon}
              color="bg-blue-600"
              to="/admin/users"
            />
            
            {isSuperAdmin && (
              <AdminMenuItem
                title="SaaS Performance"
                description="Superadmin analytics for product usage, growth, and operations"
                icon={ChartBarIcon}
                color="bg-orange-600"
                to="/admin/reports"
              />
            )}

            <AdminMenuItem
              title="Growth Settings"
              description="Configure growth rates and farming seasons"
              icon={CogIcon}
              color="bg-emerald-600"
              to="/admin/growth-settings"
            />
          </>
        )}
      </div>

      {/* System Info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">System Information</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-700">Version:</span>
            <span className="ml-2 text-gray-600">2.0.0</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Database:</span>
            <span className="ml-2 text-gray-600">MongoDB</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">API Status:</span>
            <span className="ml-2 text-green-600">● Online</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Total Locations:</span>
            <span className="ml-2 text-gray-600">Configured via admin</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminPanel: React.FC = () => {
  return (
    <Routes>
      <Route index element={<AdminMenu />} />
      {/* Admin-only routes */}
      <Route 
        path="locations" 
        element={
          <AdminOnlyRoute>
            <LocationManagement />
          </AdminOnlyRoute>
        } 
      />
      <Route 
        path="master-data" 
        element={
          <AdminOnlyRoute>
            <MasterDataManagement />
          </AdminOnlyRoute>
        } 
      />
      <Route 
        path="users" 
        element={
          <AdminOnlyRoute>
            <UserManagement />
          </AdminOnlyRoute>
        } 
      />
      <Route 
        path="reports" 
        element={
          <SuperAdminOnlyRoute>
            <SystemReports />
          </SuperAdminOnlyRoute>
        } 
      />
      <Route 
        path="growth-settings" 
        element={
          <AdminOnlyRoute>
            <GrowthSettings />
          </AdminOnlyRoute>
        } 
      />
      {/* Inventory routes - accessible to both admin and manager */}
      <Route path="inventory/purchases" element={<InventoryPurchases />} />
      <Route path="inventory/balances" element={<InventoryBalances />} />
    </Routes>
  );
};

export default AdminPanel;